import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Send, MessageSquare, Phone, Copy, CheckCircle2, Loader2,
  Mail, Link2, RotateCcw, XCircle, Clock, User, ChevronDown, ChevronUp
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const ROLE_LABELS = { admin: "Administrador", supervisor: "Supervisor", worker: "Treballador", auditor: "Auditor" };
const STATUS_CONFIG = {
  pending: { label: "Pendent", color: "bg-yellow-100 text-yellow-700" },
  sent:    { label: "Enviada",  color: "bg-blue-100 text-blue-700" },
  accepted:{ label: "Acceptada", color: "bg-emerald-100 text-emerald-700" },
  expired: { label: "Caducada", color: "bg-gray-100 text-gray-500" },
  cancelled:{ label: "Cancel·lada", color: "bg-red-100 text-red-600" },
};

const APP_URL = window.location.origin;

export default function InvitePanel({ venue, currentUser, onInviteSent }) {
  const [showForm, setShowForm] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const [form, setForm] = useState({
    invited_email: "", invited_name: "", invited_phone: "",
    role: "worker", position: "", pin: "",
  });
  const [step, setStep] = useState("form"); // form | share
  const [currentInvitation, setCurrentInvitation] = useState(null);
  const [inviteUrl, setInviteUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (venue) loadInvitations();
  }, [venue?.id]);

  const loadInvitations = async () => {
    setLoading(true);
    const all = await hosys.entities.Invitation.filter({ venue_id: venue.id });
    setInvitations(all.sort((a, b) => new Date(b.created_date) - new Date(a.created_date)));
    setLoading(false);
  };

  const handleCreateInvite = async () => {
    if (!form.invited_email || !form.role) return;
    setSending(true);
    const res = await hosys.functions.invoke("inviteWorker", {
      action: "create",
      venue_id: venue.id,
      venue_name: venue.name,
      invited_email: form.invited_email,
      invited_name: form.invited_name,
      invited_phone: form.invited_phone,
      role: form.role,
      position: form.position,
      pin: form.pin,
    });
    const { invitation, token } = res.data;
    setCurrentInvitation(invitation);
    const url = `${APP_URL}/WorkerInvite?token=${token}`;
    setInviteUrl(url);
    setStep("share");
    await loadInvitations();
    setSending(false);
  };

  const handleSendEmail = async () => {
    if (!currentInvitation) return;
    setSending(true);
    await hosys.functions.invoke("inviteWorker", {
      action: "send_email",
      invitation_id: currentInvitation.id,
      app_url: APP_URL,
    });
    await loadInvitations();
    setSending(false);
    if (onInviteSent) onInviteSent();
  };

  const handleResend = async (inv) => {
    // Create new invitation for same person
    setForm({
      invited_email: inv.invited_email,
      invited_name: inv.invited_name || "",
      invited_phone: inv.invited_phone || "",
      role: inv.role,
      position: inv.position || "",
      pin: inv.pin || "",
    });
    setStep("form");
    setShowForm(true);
  };

  const handleCancel = async (inv) => {
    await hosys.entities.Invitation.update(inv.id, { status: "cancelled" });
    await loadInvitations();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openWhatsApp = () => {
    const name = form.invited_name || currentInvitation?.invited_name || "";
    const text = encodeURIComponent(
      `Hola${name ? ` ${name}` : ""}! T'hem afegit a Ho-Sys, el sistema de gestió de ${venue.name}. Accedeix aquí per completar el teu perfil: ${inviteUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const openSMS = () => {
    const phone = form.invited_phone || currentInvitation?.invited_phone || "";
    const text = encodeURIComponent(`Ho-Sys: T'han convidat a ${venue.name}. Accedeix: ${inviteUrl}`);
    window.open(`sms:${phone}?body=${text}`, "_blank");
  };

  const resetForm = () => {
    setForm({ invited_email: "", invited_name: "", invited_phone: "", role: "worker", position: "", pin: "" });
    setStep("form");
    setCurrentInvitation(null);
    setInviteUrl("");
    setShowForm(false);
  };

  const activeInvitations = invitations.filter(i => i.status === "pending" || i.status === "sent");
  const historyInvitations = invitations.filter(i => i.status === "accepted" || i.status === "expired" || i.status === "cancelled");
  const displayHistory = showAll ? historyInvitations : historyInvitations.slice(0, 3);

  return (
    <div className="space-y-3">
      {/* Button */}
      <Button
        onClick={() => { setStep("form"); setShowForm(true); }}
        className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
      >
        <Send className="w-4 h-4" />
        Convidar treballador
      </Button>

      {/* Active invitations */}
      {activeInvitations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest px-0.5">Invitacions actives</p>
          {activeInvitations.map(inv => {
            const cfg = STATUS_CONFIG[inv.status];
            const isExpired = new Date(inv.expires_at) < new Date();
            return (
              <div key={inv.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-[#7B2D3F]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-gray-800 truncate">
                        {inv.invited_name || inv.invited_email}
                      </p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isExpired ? STATUS_CONFIG.expired.color : cfg.color}`}>
                        {isExpired ? "Caducada" : cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{inv.invited_email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] text-gray-400">
                        {ROLE_LABELS[inv.role]} {inv.position ? `· ${inv.position}` : ""}
                      </span>
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" />
                        Caduca {moment(inv.expires_at).fromNow()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleResend(inv)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Reenviar"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleCancel(inv)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Cancel·lar"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History */}
      {historyInvitations.length > 0 && (
        <div className="space-y-1.5">
          <button
            onClick={() => setShowAll(s => !s)}
            className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest px-0.5 hover:text-gray-600"
          >
            Historial ({historyInvitations.length})
            {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showAll && displayHistory.map(inv => {
            const cfg = STATUS_CONFIG[inv.status];
            return (
              <div key={inv.id} className="bg-gray-50 rounded-xl border border-gray-100 px-3 py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-600 truncate">{inv.invited_email}</p>
                  <p className="text-[10px] text-gray-400">{moment(inv.created_date).format("DD/MM/YY")}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.color}`}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {step === "form" ? "Convidar treballador" : "Compartir invitació"}
            </DialogTitle>
          </DialogHeader>

          {step === "form" && (
            <>
              <div className="space-y-3">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.invited_email}
                    onChange={e => setForm(f => ({ ...f, invited_email: e.target.value }))}
                    placeholder="treballador@exemple.com"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nom (opcional)</Label>
                    <Input
                      value={form.invited_name}
                      onChange={e => setForm(f => ({ ...f, invited_name: e.target.value }))}
                      placeholder="Joan García"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Telèfon (opcional)</Label>
                    <Input
                      type="tel"
                      value={form.invited_phone}
                      onChange={e => setForm(f => ({ ...f, invited_phone: e.target.value }))}
                      placeholder="6XX XXX XXX"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Rol *</Label>
                    <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                        <SelectItem value="worker">Treballador</SelectItem>
                        <SelectItem value="auditor">Auditor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Posició (opcional)</Label>
                    <Input
                      value={form.position}
                      onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                      placeholder="Ex: Cambrer"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>PIN kiosk (4 dígits)</Label>
                    <Input
                      type="text"
                      maxLength="4"
                      value={form.pin}
                      onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                      placeholder="0000"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={resetForm}>Cancel·lar</Button>
                <Button
                  onClick={handleCreateInvite}
                  disabled={sending || !form.invited_email || !form.role}
                  className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Generar invitació →
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "share" && (
            <>
              <div className="space-y-4">
                {/* Success notice */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  Invitació creada! Comparteix-la per:
                </div>

                {/* Share options */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={handleSendEmail}
                    disabled={sending}
                    className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-xl transition-all active:scale-95"
                  >
                    {sending
                      ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                      : <Mail className="w-6 h-6 text-blue-500" />
                    }
                    <span className="text-xs font-semibold text-blue-700">Email</span>
                  </button>
                  <button
                    onClick={openWhatsApp}
                    className="flex flex-col items-center gap-2 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 rounded-xl transition-all active:scale-95"
                  >
                    <MessageSquare className="w-6 h-6 text-emerald-500" />
                    <span className="text-xs font-semibold text-emerald-700">WhatsApp</span>
                  </button>
                  <button
                    onClick={openSMS}
                    className="flex flex-col items-center gap-2 p-4 bg-purple-50 hover:bg-purple-100 border border-purple-100 rounded-xl transition-all active:scale-95"
                  >
                    <Phone className="w-6 h-6 text-purple-500" />
                    <span className="text-xs font-semibold text-purple-700">SMS</span>
                  </button>
                </div>

                {/* Link */}
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Link d'invitació</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-600 flex-1 truncate font-mono">{inviteUrl}</p>
                    <button
                      onClick={copyLink}
                      className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${copied ? "bg-emerald-100 text-emerald-600" : "bg-gray-200 text-gray-500 hover:bg-gray-300"}`}
                    >
                      {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Caduca en 7 dies. El treballador rebrà un email per accedir al sistema.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={resetForm} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] w-full">
                  Tancar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}